import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { AfterViewInit, Component, ElementRef, Input, OnInit, Output, EventEmitter, OnDestroy, HostBinding, Optional, Self } from '@angular/core';
import { AbstractControl, ControlValueAccessor, UntypedFormControl, NgControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-address-autocomplete',
    templateUrl: './address-autocomplete.component.html',
    styleUrls: ['./address-autocomplete.component.scss'],
    providers: [
        { provide: MatFormFieldControl, useExisting: AddressAutocompleteComponent }
    ]
})
export class AddressAutocompleteComponent implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor, MatFormFieldControl<string> {

    static nextId = 0;
    @Input() ensureSelection = false;
    @Output() placeChanged: EventEmitter<google.maps.places.PlaceResult> = new EventEmitter();
    @HostBinding() id: string = `address-autocomplete-input-${AddressAutocompleteComponent.nextId++}`;
    @HostBinding('attr.aria-describedby') describedBy = '';

    stateChanges: Subject<void> = new Subject<void>();
    focused: boolean = false;
    controlType: string = 'address-autocomplete-input';
    disabled: boolean = false;
    autofilled?: boolean;
    validateFn: ValidatorFn;

    private isSelected: boolean;
    private autocomplete: google.maps.places.Autocomplete;
    private _value = null;
    private _placeholder: string = '';
    private _required: boolean = false;

    constructor(
        @Optional() @Self() public ngControl: NgControl,
        private fm: FocusMonitor,
        private elRef: ElementRef<HTMLElement>
    ) {
        this.fm.monitor(this.elRef, true).subscribe(origin => {
            if (this.focused && !origin) {
                this.onTouched();
            }
            this.focused = !!origin;
            this.stateChanges.next();
        });
        if (this.ngControl != null) { // it replaces the NG_VALUE_ACCESSOR
            this.ngControl.valueAccessor = this;
        }
    }

    @HostBinding('class.floating') get shouldLabelFloat() { return this.focused || !this.empty; }

    @Input() // value
    get value(): string | null { return this._value; }
    set value(val: string | null) {
        this._value = val;
        this.onChange(this._value);
        this.stateChanges.next();
    }

    @Input() // placeholder
    get placeholder(): string { return this._placeholder; }
    set placeholder(placeHolder: string) {
        this._placeholder = placeHolder;
        this.stateChanges.next();
    }

    @Input() // required
    get required(): boolean { return this._required; }
    set required(value: boolean) {
        this._required = coerceBooleanProperty(value);
        this.stateChanges.next();
    }

    get empty(): boolean {
        return !this.value;
    }

    get errorState() {
        return this.ngControl.errors !== null && !!this.ngControl.touched;
    }

    get input(): HTMLInputElement {
        return this.elRef.nativeElement.querySelector('input');
    }

    ngOnInit(): void {
        this.isSelected = !!this.ngControl.value;
        this.validateFn = this.getValidator();
        if (this.ngControl && this.ngControl.control) {
            this.ngControl.control.setValidators(Validators.compose([this.ngControl.control.validator, this.validateFn]));
        }
    }

    ngAfterViewInit() {
        if (!this.autocomplete) {
            this.autocomplete = new google.maps.places.Autocomplete(this.input, {
                types: ['address']
            });
            this.autocomplete.addListener('place_changed', () => {
                this.isSelected = true;
                this.ngControl.control.updateValueAndValidity({ onlySelf: true });
                this.placeChanged.emit(this.autocomplete.getPlace());
            });
        }
    }

    ngOnDestroy() {
        this.stateChanges.complete();
        this.fm.stopMonitoring(this.elRef);
    }

    onValueChanges(event: Event) {
        this.isSelected = false;
        this.value = (<HTMLInputElement>event.target).value;
    }

    onChange = (_: any) => { };
    onTouched = () => { };

    writeValue(value: string): void {
        if (value) {
            this.value = value;
        }
    }
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }
    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    setDescribedByIds(ids: string[]): void {
        this.describedBy = ids.join(' ');
    }
    onContainerClick(event: MouseEvent) {
        if ((event.target as Element).tagName.toLowerCase() !== 'input') {
            this.input.focus();
        }
    }
    getFormControl(): UntypedFormControl {
        return this.ngControl.control as UntypedFormControl;
    }

    private getValidator(): ValidatorFn {
        return (_: AbstractControl): ValidationErrors | null => {
            return this.ensureSelection && !this.isSelected ? { 'notSelected': true } : null;
        };
    }
}
